export type CUSTOMER_FEEDBACK_ACTION =
  | 'changed_inquiry_status'
  | 'responded_to_inquiry'
  | 'saved_menu'
  | 'saved_menu_item'
  | 'saved_hours'
  | 'saved_restriction'

/**
 * InquiryListSurvey and InquiryDetailsSurvey own the inquiry pages. When one of them is actually
 * going to appear, the host's survey stays out of the way rather than stacking a second panel.
 *
 * "Actually going to appear" is only known once their own endpoint has answered — it returns 204
 * for anyone who has already seen or answered that survey — so this is a gate rather than a flag.
 * Those components open it when their check finishes, and the trigger below waits on it.
 */
const CHECK_TIMEOUT_MS = 5000

let suppressed = false
let gate: Promise<void> | null = null
let openGate: (() => void) | null = null

/** Called before the survey's request starts, so a trigger in the same tick knows to wait. */
export function beginCustomerFeedbackCheck() {
  gate = new Promise<void>((resolve) => {
    openGate = resolve
  })
}

/** Called once the survey knows whether it has something to show. */
export function endCustomerFeedbackCheck(willShow: boolean) {
  if (willShow) suppressed = true
  openGate?.()
  openGate = null
}

/**
 * Tells the host admin a key action just succeeded, so it can offer its one-time survey.
 *
 * The host decides whether to ask — this is only a notification, and it must never interrupt the
 * action the user just completed.
 *
 * Deferred by a macrotask so a survey mounting in response to the same action gets to open its
 * check first: the flag that mounts it is set in the same handler that calls this, and Vue renders
 * on the microtask queue.
 */
export function triggerCustomerFeedback(action: CUSTOMER_FEEDBACK_ACTION) {
  setTimeout(async () => {
    // Timed, so a survey that errors between begin and end cannot silence this one for good.
    if (gate) await Promise.race([gate, sleep(CHECK_TIMEOUT_MS)])
    if (suppressed) return

    try {
      bridge.sendMessage({
        name: 'customer-feedback-trigger',
        payload: { action },
      })
    } catch {
      /* empty */
    }
  }, 0)
}

function sleep(ms: number) {
  return new Promise((resolve) => setTimeout(resolve, ms))
}
